import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, AlertCircle, TrendingDown, TrendingUp, 
  Clock, Bell, BellOff, Volume2, VolumeX, RefreshCw,
  X, ChevronDown, ChevronUp, Brain, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import soundManager from '@/utils/soundManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Market hours (Eastern Time)
const MARKET_OPEN_HOUR = 9;
const MARKET_OPEN_MINUTE = 30;
const MARKET_CLOSE_HOUR = 16;
const MARKET_CLOSE_MINUTE = 0;
const WARNING_MINUTES_BEFORE_CLOSE = 10;

// Sound effects using Web Audio API
const createSoundEffects = () => {
  let audioContext = null;
  
  const getContext = async () => {
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (browser policy requires user interaction)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    return audioContext;
  };

  // URGENT SIREN - Longer, more intense, warbling sound
  const playUrgentSiren = async () => {
    const ctx = await getContext();
    const duration = 4; // 4 seconds - much longer!
    
    // Create multiple oscillators for a more intense sound
    for (let i = 0; i < 3; i++) {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Different starting frequencies for richness
      const baseFreq = 600 + (i * 100);
      oscillator.type = i === 0 ? 'sawtooth' : 'square';
      
      // Create warbling effect - rapid frequency changes
      const startTime = ctx.currentTime + (i * 0.1);
      oscillator.frequency.setValueAtTime(baseFreq, startTime);
      
      // Multiple up-down cycles for urgent feel
      for (let cycle = 0; cycle < 8; cycle++) {
        const cycleStart = startTime + (cycle * 0.5);
        oscillator.frequency.linearRampToValueAtTime(baseFreq + 400, cycleStart + 0.25);
        oscillator.frequency.linearRampToValueAtTime(baseFreq, cycleStart + 0.5);
      }
      
      // Volume envelope
      gainNode.gain.setValueAtTime(0.25 - (i * 0.05), startTime);
      gainNode.gain.setValueAtTime(0.25 - (i * 0.05), startTime + duration - 0.5);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    }
  };

  // BELL SOUND - Classic trading bell
  const playBell = async () => {
    const ctx = await getContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(1200, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.3);
    
    gainNode.gain.setValueAtTime(0.4, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.5);
  };

  // RAPID BELLS - 5 bells in quick succession
  const playRapidBells = async (count = 5) => {
    const ctx = await getContext();
    
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.type = 'sine';
        // Slightly different pitch each time for richness
        const pitch = 1200 + (Math.random() * 200 - 100);
        oscillator.frequency.setValueAtTime(pitch, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(pitch * 0.7, ctx.currentTime + 0.25);
        
        gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.4);
      }, i * 150); // 150ms apart - fast!
    }
  };

  // WARNING HORN - Deep foghorn/warning sound (replaces thunder)
  const playWarningHorn = async () => {
    const ctx = await getContext();
    const duration = 1.5;
    
    // Main foghorn tone
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const osc3 = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    // Deep base frequency with harmonics
    osc1.type = 'sawtooth';
    osc1.frequency.setValueAtTime(110, ctx.currentTime); // A2 - deep
    
    osc2.type = 'square';
    osc2.frequency.setValueAtTime(55, ctx.currentTime); // A1 - very deep
    
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(220, ctx.currentTime); // A3 - overtone
    
    // Create gain nodes for mixing
    const gain1 = ctx.createGain();
    const gain2 = ctx.createGain();
    const gain3 = ctx.createGain();
    
    gain1.gain.setValueAtTime(0.4, ctx.currentTime);
    gain2.gain.setValueAtTime(0.3, ctx.currentTime);
    gain3.gain.setValueAtTime(0.15, ctx.currentTime);
    
    osc1.connect(gain1);
    osc2.connect(gain2);
    osc3.connect(gain3);
    
    gain1.connect(gainNode);
    gain2.connect(gainNode);
    gain3.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Envelope - starts strong, fades
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.6, ctx.currentTime + 0.1);
    gainNode.gain.setValueAtTime(0.6, ctx.currentTime + duration - 0.3);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    
    osc1.start(ctx.currentTime);
    osc2.start(ctx.currentTime);
    osc3.start(ctx.currentTime);
    
    osc1.stop(ctx.currentTime + duration);
    osc2.stop(ctx.currentTime + duration);
    osc3.stop(ctx.currentTime + duration);
  };

  // TRIPLE WARNING HORN - 3 horns with gaps
  const playTripleWarningHorn = async () => {
    await playWarningHorn();
    setTimeout(() => playWarningHorn(), 2000);
    setTimeout(() => playWarningHorn(), 4000);
  };

  return { 
    playUrgentSiren, 
    playBell, 
    playRapidBells, 
    playWarningHorn, 
    playTripleWarningHorn,
    getContext 
  };
};

// Get current time in Eastern timezone
const getEasternTime = () => {
  const now = new Date();
  const eastern = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  return eastern;
};

// Check if market is open (weekday, 9:30 AM - 4:00 PM ET)
const isMarketOpen = () => {
  const et = getEasternTime();
  const day = et.getDay();
  const hour = et.getHours();
  const minute = et.getMinutes();
  
  // Weekend check
  if (day === 0 || day === 6) return false;
  
  const timeInMinutes = hour * 60 + minute;
  const openTime = MARKET_OPEN_HOUR * 60 + MARKET_OPEN_MINUTE;
  const closeTime = MARKET_CLOSE_HOUR * 60 + MARKET_CLOSE_MINUTE;
  
  return timeInMinutes >= openTime && timeInMinutes < closeTime;
};

const PortfolioHealthMonitor = ({ 
  positions = [], 
  spreads = [], 
  isConnected = false,
  onPositionAction 
}) => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [hasCritical, setHasCritical] = useState(false);
  const [marketStatus, setMarketStatus] = useState('closed');
  
  const audioRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const marketTimerRef = useRef(null);
  const lastAlertHashRef = useRef(null);
  const lastMarketEventRef = useRef(null);

  // Initialize audio immediately on mount
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = createSoundEffects();
    }
  }, []);

  // Market time checker - runs every 30 seconds
  useEffect(() => {
    const checkMarketTime = () => {
      const et = getEasternTime();
      const day = et.getDay();
      const hour = et.getHours();
      const minute = et.getMinutes();
      
      // Skip weekends
      if (day === 0 || day === 6) {
        setMarketStatus('weekend');
        return;
      }
      
      const timeInMinutes = hour * 60 + minute;
      const openTime = MARKET_OPEN_HOUR * 60 + MARKET_OPEN_MINUTE;
      const closeTime = MARKET_CLOSE_HOUR * 60 + MARKET_CLOSE_MINUTE;
      const warningTime = closeTime - WARNING_MINUTES_BEFORE_CLOSE;
      
      const eventKey = `${day}-${hour}-${minute}`;
      
      // Market OPEN - 9:30 AM ET
      if (timeInMinutes === openTime && lastMarketEventRef.current !== `open-${eventKey}`) {
        lastMarketEventRef.current = `open-${eventKey}`;
        setMarketStatus('open');
        if (soundEnabled && audioRef.current) {
          audioRef.current.playRapidBells(5);
          toast.success('🔔 MARKET OPEN!', {
            duration: 10000,
            description: 'Trading session has begun!'
          });
        }
      }
      // 10 minutes before close - 3:50 PM ET - WARNING HORN
      else if (timeInMinutes === warningTime && lastMarketEventRef.current !== `warning-${eventKey}`) {
        lastMarketEventRef.current = `warning-${eventKey}`;
        setMarketStatus('closing-soon');
        if (soundEnabled && audioRef.current) {
          audioRef.current.playTripleWarningHorn();
          toast.warning('📢 10 MINUTES TO CLOSE!', {
            duration: 15000,
            description: 'Market closing soon - review your positions!'
          });
        }
      }
      // Market CLOSE - 4:00 PM ET
      else if (timeInMinutes === closeTime && lastMarketEventRef.current !== `close-${eventKey}`) {
        lastMarketEventRef.current = `close-${eventKey}`;
        setMarketStatus('closed');
        if (soundEnabled && audioRef.current) {
          audioRef.current.playRapidBells(5);
          toast.info('🔔 MARKET CLOSED!', {
            duration: 10000,
            description: 'Trading session has ended.'
          });
        }
      }
      // Normal market hours
      else if (timeInMinutes > openTime && timeInMinutes < warningTime) {
        setMarketStatus('open');
      }
      else if (timeInMinutes >= warningTime && timeInMinutes < closeTime) {
        setMarketStatus('closing-soon');
      }
      else {
        setMarketStatus('closed');
      }
    };
    
    // Initial check
    checkMarketTime();
    
    // Check every 30 seconds
    marketTimerRef.current = setInterval(checkMarketTime, 30000);
    
    return () => {
      if (marketTimerRef.current) {
        clearInterval(marketTimerRef.current);
      }
    };
  }, [soundEnabled]);

  // Play urgent siren sound - Uses 999 Social Credit Siren from myinstants
  const playSiren = useCallback(() => {
    if (soundEnabled) {
      try {
        // Use the 999 Social Credit Siren for urgent alerts
        soundManager.playUrgentAlert();
      } catch (e) {
        console.error('Error playing sound:', e);
      }
    }
  }, [soundEnabled]);

  // Check portfolio health - useAI parameter controls whether to include AI analysis
  const checkHealth = useCallback(async (useAI = false) => {
    if (!isConnected || (positions.length === 0 && spreads.length === 0)) {
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/portfolio/health-check`, {
        positions: positions,
        spreads: spreads,
        loss_threshold: -50.0,
        profit_threshold: 100.0,
        expiration_warning_days: 7,
        include_ai_analysis: useAI  // Only include AI when manually requested
      });

      if (response.data.success) {
        const newAlerts = response.data.alerts || [];
        setAlerts(newAlerts);
        if (useAI) {
          setAiSummary(response.data.ai_summary);
        }
        setLastCheck(new Date());
        setHasCritical(response.data.has_critical);

        // Create hash of critical alerts to avoid repeated sounds
        const criticalHash = newAlerts
          .filter(a => a.level === 'critical')
          .map(a => a.position)
          .join(',');

        // Play sound only if there are new critical alerts
        if (response.data.play_sound && criticalHash !== lastAlertHashRef.current) {
          playSiren();
          lastAlertHashRef.current = criticalHash;
          
          // Also show a toast notification
          toast.error('🚨 CRITICAL: Portfolio needs attention!', {
            duration: 10000,
            description: `${response.data.critical_count} position(s) in trouble`
          });
        }
      }
    } catch (error) {
      console.error('Health check error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [positions, spreads, isConnected, playSiren]);

  // Auto-refresh every minute (without AI to save resources)
  useEffect(() => {
    if (autoRefresh && isConnected) {
      // Initial check (no AI)
      checkHealth(false);
      
      // Set up interval (every 60 seconds, no AI)
      refreshIntervalRef.current = setInterval(() => checkHealth(false), 60000);
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, isConnected, checkHealth]);

  // Get alert icon based on type
  const getAlertIcon = (type, level) => {
    if (level === 'critical') {
      return <AlertTriangle className="h-5 w-5 text-red-500 animate-pulse" />;
    }
    switch (type) {
      case 'losing_big':
      case 'losing_money':
        return <TrendingDown className="h-5 w-5 text-red-500" />;
      case 'winning_big':
        return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'near_expiration':
      case 'expired':
        return <Clock className="h-5 w-5 text-orange-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  // Get badge color based on level
  const getBadgeClass = (level) => {
    switch (level) {
      case 'critical':
        return 'bg-red-500 text-white animate-pulse';
      case 'warning':
        return 'bg-orange-500 text-white';
      case 'info':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  // Don't render if not connected or no positions
  if (!isConnected || (positions.length === 0 && spreads.length === 0)) {
    return null;
  }

  const criticalAlerts = alerts.filter(a => a.level === 'critical');
  const warningAlerts = alerts.filter(a => a.level === 'warning');
  const infoAlerts = alerts.filter(a => a.level === 'info');

  return (
    <div className="space-y-4" data-testid="portfolio-health-monitor">
      {/* Critical Alert Banner - Always visible when there are critical alerts */}
      {criticalAlerts.length > 0 && (
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg p-4 shadow-lg animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-8 w-8" />
              <div>
                <h3 className="text-lg font-bold">🚨 CRITICAL ALERT</h3>
                <p className="text-sm opacity-90">
                  {criticalAlerts.length} position{criticalAlerts.length > 1 ? 's' : ''} need immediate attention!
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="bg-white/20 border-white/30 text-white hover:bg-white/30"
              >
                {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Health Monitor Card */}
      <Card className={`glass-effect border-0 shadow-lg ${hasCritical ? 'border-2 border-red-500' : ''}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center">
              <CardTitle className="flex items-center text-lg">
                <Zap className={`h-5 w-5 mr-2 ${hasCritical ? 'text-red-500 animate-pulse' : 'text-indigo-600'}`} />
                Portfolio Health Monitor
              </CardTitle>
              
              {/* Market Status Badge */}
              <Badge 
                className={`ml-3 ${
                  marketStatus === 'open' 
                    ? 'bg-green-500 text-white' 
                    : marketStatus === 'closing-soon'
                      ? 'bg-orange-500 text-white animate-pulse'
                      : marketStatus === 'weekend'
                        ? 'bg-gray-500 text-white'
                        : 'bg-red-500 text-white'
                }`}
              >
                {marketStatus === 'open' && '🟢 MARKET OPEN'}
                {marketStatus === 'closing-soon' && '⚡ CLOSING SOON'}
                {marketStatus === 'closed' && '🔴 MARKET CLOSED'}
                {marketStatus === 'weekend' && '📅 WEEKEND'}
              </Badge>
              
              {alerts.length > 0 && (
                <Badge className="ml-2 bg-indigo-100 text-indigo-700">
                  {alerts.length} alert{alerts.length > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Auto-refresh toggle */}
              <div className="flex items-center space-x-2">
                <Label htmlFor="auto-refresh" className="text-xs text-gray-500">
                  Auto (1min)
                </Label>
                <Switch
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                  data-testid="health-auto-refresh"
                />
              </div>
              
              {/* Sound toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={soundEnabled ? 'text-indigo-600' : 'text-gray-400'}
                title={soundEnabled ? 'Sound enabled' : 'Sound muted'}
              >
                {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>
              
              {/* Manual refresh - includes AI analysis */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => checkHealth(true)}
                disabled={isLoading}
                data-testid="health-refresh-btn"
                title="Refresh with AI analysis"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
              
              {/* Expand/collapse */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          
          {lastCheck && (
            <p className="text-xs text-gray-500 mt-1">
              Last checked: {lastCheck.toLocaleTimeString()}
            </p>
          )}
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-2">
            {alerts.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No alerts - Your portfolio looks healthy!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* AI Summary */}
                {aiSummary && criticalAlerts.length > 0 && (
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-start space-x-2">
                      <Brain className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-purple-800 mb-1">AI Analysis</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiSummary}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Critical Alerts */}
                {criticalAlerts.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-red-700 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Critical ({criticalAlerts.length})
                    </h4>
                    {criticalAlerts.map((alert, idx) => (
                      <AlertRow 
                        key={`critical-${idx}`} 
                        alert={alert} 
                        onAnalyze={onPositionAction}
                        getBadgeClass={getBadgeClass}
                        getAlertIcon={getAlertIcon}
                      />
                    ))}
                  </div>
                )}

                {/* Warning Alerts */}
                {warningAlerts.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-orange-700 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Warnings ({warningAlerts.length})
                    </h4>
                    {warningAlerts.map((alert, idx) => (
                      <AlertRow 
                        key={`warning-${idx}`} 
                        alert={alert} 
                        onAnalyze={onPositionAction}
                        getBadgeClass={getBadgeClass}
                        getAlertIcon={getAlertIcon}
                      />
                    ))}
                  </div>
                )}

                {/* Info Alerts */}
                {infoAlerts.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-blue-700 flex items-center">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Info ({infoAlerts.length})
                    </h4>
                    {infoAlerts.map((alert, idx) => (
                      <AlertRow 
                        key={`info-${idx}`} 
                        alert={alert} 
                        onAnalyze={onPositionAction}
                        getBadgeClass={getBadgeClass}
                        getAlertIcon={getAlertIcon}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Sound Test Section */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Test Sounds (click to preview):</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    if (audioRef.current) {
                      await audioRef.current.playUrgentSiren();
                    }
                  }}
                  className="text-xs hover:bg-red-50"
                  data-testid="test-siren"
                >
                  🚨 Siren
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    if (audioRef.current) {
                      await audioRef.current.playRapidBells(5);
                    }
                  }}
                  className="text-xs hover:bg-yellow-50"
                  data-testid="test-bells"
                >
                  🔔 Bells (x5)
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    if (audioRef.current) {
                      await audioRef.current.playTripleWarningHorn();
                    }
                  }}
                  className="text-xs hover:bg-purple-50"
                  data-testid="test-horn"
                >
                  📢 Horn (x3)
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

// Individual alert row component
const AlertRow = ({ alert, onAnalyze, getBadgeClass, getAlertIcon }) => {
  const handleAnalyze = () => {
    if (onAnalyze && alert.position_data) {
      onAnalyze({
        ...alert.position_data,
        _actionTimestamp: Date.now()
      }, 'analyze');
    }
  };

  // Get platform/source display
  const getSourceLabel = () => {
    const source = alert.position_data?.source;
    const platform = alert.position_data?.platform;
    
    if (source === 'imported' && platform) {
      return platform.charAt(0).toUpperCase() + platform.slice(1);
    } else if (source === 'imported') {
      return 'Imported';
    }
    return 'Tastytrade';
  };

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${
      alert.level === 'critical' 
        ? 'bg-red-50 border-red-200' 
        : alert.level === 'warning'
          ? 'bg-orange-50 border-orange-200'
          : 'bg-blue-50 border-blue-200'
    }`}>
      <div className="flex items-center space-x-3">
        {getAlertIcon(alert.alerts[0]?.type, alert.level)}
        <div>
          <div className="flex items-center space-x-2 flex-wrap gap-1">
            <span className="font-semibold">{alert.position}</span>
            <Badge className={getBadgeClass(alert.level)}>
              {alert.level.toUpperCase()}
            </Badge>
            <Badge variant="outline" className="text-xs bg-gray-100">
              {getSourceLabel()}
            </Badge>
          </div>
          <div className="text-sm text-gray-600 space-x-2">
            {alert.alerts.map((a, i) => (
              <span key={i}>{a.message}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <span className={`font-bold ${alert.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {alert.pnl >= 0 ? '+' : ''}${alert.pnl?.toFixed(2)}
        </span>
        {onAnalyze && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleAnalyze}
            className="text-xs"
            data-testid={`analyze-alert-${alert.symbol}`}
          >
            <Brain className="h-3 w-3 mr-1" />
            Analyze
          </Button>
        )}
      </div>
    </div>
  );
};

export default PortfolioHealthMonitor;
